import {
  AccountCircle as ProfileIcon,
  Category,
  CompareArrows as ComparisonAnalyticsIcon,
  ControlCamera as CommandCenterIcon,
  Devices as DevicesIcon,
  Inventory as InventoryIcon,
  LockOutlined,
  ManageSearch as ShopperDetailsIcon,
  People as CustomersIcon,
  Phonelink as MobileLinksIcon,
  QueryStats as DataConsistencyIcon,
  Router as RFIDIcon,
  Science as ScienceIcon,
  Sensors as CentralRfidActivityIcon,
  Settings as ConfigsIcon,
  Storage as ETLIcon,
  Store as StoresIcon,
  VideogameAsset as SimulatorIcon,
  VpnKey as ApiKeysIcon,
  Webhook as WebhookIcon,
} from "@mui/icons-material";
import { Box, Divider, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { SidebarItem } from "@/components/layout/Sidebar/SidebarItem";
import { SidebarSection } from "@/components/layout/Sidebar/SidebarSection";
import { paths } from "@/routes/paths";
import { UserRole } from "@/utils/roleManager";

interface CraveAdminSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  userHasPermission: (role: UserRole) => boolean;
  isMobile?: boolean;
  onItemSelect?: () => void;
}

export const CraveAdminSection: React.FC<CraveAdminSectionProps> = ({
  isExpanded,
  onToggle,
  userHasPermission,
  isMobile = false,
  onItemSelect,
}) => {
  const { t } = useTranslation();

  const canSeeCraveAdmin = [UserRole.CRAVE_EMPLOYEE, UserRole.CRAVE_ADMIN].some((role) =>
    userHasPermission(role),
  );

  if (!canSeeCraveAdmin) {
    return null;
  }

  return (
    <SidebarSection
      title={t("nav.sections.craveAdmin")}
      isExpanded={isExpanded}
      onToggle={onToggle}
      isMobile={isMobile}
    >
      <SidebarItem
        label={t("commandCenter.title")}
        path={paths.admin.commandCenter}
        icon={<CommandCenterIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("nav.customers")}
        path={paths.admin.customers}
        icon={<CustomersIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("nav.stores")}
        path={paths.admin.stores}
        icon={<StoresIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <Divider sx={{ my: 1, mx: 2 }} />

      {/* Hardware & Infrastructure */}
      <SidebarItem
        label={t("nav.devices")}
        path={paths.admin.devices}
        icon={<DevicesIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.craveAdminSection.fields.rfidReaders")}
        path={paths.admin.rfidReaders}
        icon={<RFIDIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.craveAdminSection.fields.centralRfidActivity")}
        path={paths.admin.centralRfidActivity}
        icon={<CentralRfidActivityIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />

      <Divider sx={{ my: 1, mx: 2 }} />

      {/* Data & Integration */}
      <SidebarItem
        label="Single sign-on"
        path={paths.admin.sso}
        icon={<LockOutlined />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.craveAdminSection.fields.etlRuns")}
        path={paths.admin.etlRuns}
        icon={<ETLIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.craveAdminSection.fields.dataConsistency")}
        path={paths.admin.dataConsistency}
        icon={<DataConsistencyIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.craveAdminSection.fields.shopperDetailsExport")}
        path={paths.admin.shopperDetails}
        icon={<ShopperDetailsIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("management.webhooks.webhooks.title")}
        path={paths.admin.webhooks}
        icon={<WebhookIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("nav.apiKeys")}
        path={paths.admin.apiKeys}
        icon={<ApiKeysIcon />}
        minimumRole={UserRole.CRAVE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />

      <Divider sx={{ my: 1, mx: 2 }} />

      {/* Configuration & Categories */}
      <SidebarItem
        label={t("layout.sidebar.craveAdminSection.fields.configs")}
        path={paths.admin.configs}
        icon={<ConfigsIcon />}
        minimumRole={UserRole.CRAVE_ADMIN}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("management.mobileLink.mobileLinks.title")}
        path={paths.admin.mobileLinks}
        icon={<MobileLinksIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("layout.sidebar.craveAdminSection.fields.categories")}
        path={`${paths.admin.categories}/customer-categories`}
        icon={<Category />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <Divider sx={{ my: 1, mx: 2 }} />

      {/* Development Tools */}
      <SidebarItem
        label={t("layout.sidebar.craveAdminSection.fields.simulator")}
        path={paths.admin.simulator}
        icon={<SimulatorIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />

      {/* Experimental Section Divider */}
      <Box sx={{ display: "flex", alignItems: "center", my: 1, mx: 2 }}>
        <Divider sx={{ flex: 1 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1 }}>
          <ScienceIcon sx={{ fontSize: 12, opacity: 0.6 }} />
          <Typography
            variant="caption"
            sx={{ fontSize: 10, opacity: 0.6, textTransform: "uppercase" }}
          >
            {t("layout.sidebar.craveAdminSection.fields.experimental")}
          </Typography>
        </Box>
        <Divider sx={{ flex: 1 }} />
      </Box>

      {/* Experimental Features */}
      <SidebarItem
        label={t("nav.inventory")}
        path={paths.admin.inventory}
        icon={<InventoryIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("comparisonAnalytics.title")}
        path={paths.admin.comparisonAnalytics}
        icon={<ComparisonAnalyticsIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
      <SidebarItem
        label={t("common.profile")}
        path={paths.utilities.profile}
        icon={<ProfileIcon />}
        minimumRole={UserRole.CRAVE_EMPLOYEE}
        userHasPermission={userHasPermission}
        isMobile={isMobile}
        afterSelect={onItemSelect}
      />
    </SidebarSection>
  );
};
